import React, { useEffect, useRef } from 'react';
import { MessageSquare, Clock, Shield } from 'lucide-react';
import { features } from '../data/mockData';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
  MessageSquare: MessageSquare,
  Clock: Clock,
  Shield: Shield,
};

const MeetTheBros = () => {
  const sectionRef = useRef(null);
  const logoRef = useRef(null);
  const contentRef = useRef(null);
  const featuresRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Logo slide in from left
      gsap.fromTo(logoRef.current,
        { x: -200, opacity: 0, rotation: -10 },
        {
          x: 0,
          opacity: 1,
          rotation: 0,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Content fade in from right
      gsap.fromTo(contentRef.current,
        { x: 100, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Features stagger animation
      const featureItems = featuresRef.current?.querySelectorAll('.feature-item');
      if (featureItems) {
        gsap.fromTo(featureItems,
          { x: 50, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 0.6,
            stagger: 0.2,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: featuresRef.current,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="team" className="py-20 bg-white overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Logo/Image with slide-in animation */}
          <div className="flex justify-center">
            <div ref={logoRef}>
              <img 
                src="https://customer-assets.emergentagent.com/job_green-funding-bros/artifacts/u2sxm37r_full%20logo.png"
                alt="Waste Loan Bros"
                className="w-[432px] h-auto md:w-[518px] object-contain"
              />
            </div>
          </div>

          {/* Content */}
          <div ref={contentRef}>
            <span className="text-[#00A86B] font-bold text-sm tracking-wider">MEET THE BROS</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#0A1628] mt-4 mb-2">
              WASTE GUYS
            </h2>
            <h2 className="text-4xl md:text-5xl font-black text-[#00A86B] mb-6">
              WHO GET IT
            </h2>
            <div className="w-16 h-1 bg-[#00A86B] mb-6"></div>
            
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              We're not bankers in suits who've never touched a garbage truck. We're former waste management contractors who built our business from the ground up—just like you. We understand the challenges, the cash flow cycles, and the opportunities that come with this industry.
            </p>

            {/* Features */}
            <div ref={featuresRef} className="space-y-6">
              {features.map((feature, index) => {
                const IconComponent = iconMap[feature.icon];
                return (
                  <div key={index} className="feature-item flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#00A86B] rounded-full flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0A1628] text-lg">{feature.title}</h4>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MeetTheBros;
